import React, { useEffect, useState } from "react";
import api from "../api";
import "./estilodash.css";

const ListarPagamentos = () => {
  const [pagamentos, setPagamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [planos, setPlanos] = useState([]);

  const [clienteIdSelecionado, setClienteIdSelecionado] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    carregarTudo();
  }, []);

  // =====================
  // CARREGAR DADOS
  // =====================

  const carregarTudo = async () => {
    try {
      setCarregando(true);
      setMsg("");

      const [respPag, respCli, respPlanos] = await Promise.all([
        api.get("/api/pagamentos"),
        api.get("/api/clientes"),
        api.get("/api/planos-ativos"),
      ]);

      setPagamentos(respPag.data || []);
      setClientes(respCli.data || []);
      setPlanos(respPlanos.data || []);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      setMsg("Erro ao carregar pagamentos/clientes. Verifique login/servidor.");
    } finally {
      setCarregando(false);
    }
  };

  const getCliente = (id) => clientes.find((c) => Number(c.id) === Number(id));

  const getPlano = (id) => planos.find((p) => Number(p.id) === Number(id));

  const formatData = (dataStr) => {
    if (!dataStr) return "—";
    try {
      const d = new Date(dataStr);
      if (Number.isNaN(d.getTime())) return dataStr;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return dataStr;
    }
  };

  // ===================================================
  // CÁLCULO DO VALOR CALCULADO (sempre a partir do plano)
  // ===================================================

  const computeValorCalculado = (p) => {
    const cliente = getCliente(p.cliente_id);
    const plano = cliente ? getPlano(cliente.plano_id) : null;

    if (!cliente || !plano) {
      // se não tiver info suficiente, volta o que veio do banco (se existir)
      return Number(p.valor_calculado || 0);
    }

    const ativo = String(cliente.status) === "1";
    const isentoCli = String(cliente.isento) === "1";

    // inativo ou isento => cobrança 0
    if (!ativo || isentoCli) {
      return 0;
    }

    const slotsCobrados =
      p.slots_cobrados != null
        ? Number(p.slots_cobrados)
        : Number(cliente.slots_contratados || 0);

    const slotsIncluidos = Number(plano.slots_inclusos || 0);
    const base = Number(plano.valor_base || 0);
    const extraSlot = Number(plano.valor_slot_extra || 0);

    const extras = Math.max(slotsCobrados - slotsIncluidos, 0);
    const valor = base + extras * extraSlot;

    return valor;
  };

  // =============================
  // MARCAR COMO PAGO (usa plano + grava PIX)
  // =============================

  const marcarComoPago = async (pag) => {
    const cliente = getCliente(pag.cliente_id);
    const nomeCli = cliente ? cliente.nome : pag.cliente_id;

    const valorCalculado = computeValorCalculado(pag);
    const valorSugestao =
      valorCalculado > 0 ? valorCalculado : Number(pag.valor || 0);

    if (
      !window.confirm(
        `Marcar pagamento do cliente "${nomeCli}" para o mês ${
          pag.mes_referencia
        } como PAGO no valor de R$ ${valorSugestao.toFixed(2)} (método: PIX)?`
      )
    ) {
      return;
    }

    try {
      // ✅ ao marcar como pago, gravar a data REAL do pagamento (hoje)
      const hoje = new Date();
      const yyyy = hoje.getFullYear();
      const mm = String(hoje.getMonth() + 1).padStart(2, "0");
      const dd = String(hoje.getDate()).padStart(2, "0");
      const dataPagamentoStr = `${yyyy}-${mm}-${dd}`;

      const body = {
        cliente_id: pag.cliente_id,
        data_pagamento: dataPagamentoStr,
        valor: Number(valorSugestao.toFixed(2)),
        descricao: pag.descricao || "",
        mes_referencia: pag.mes_referencia,
        metodo_pagamento: "PIX",
        status: "Pago",
      };

      await api.put(`/api/pagamentos/${pag.id}`, body);

      await carregarTudo();
    } catch (err) {
      console.error("Erro ao marcar como pago:", err);
      alert("Erro ao marcar como pago.");
    }
  };

  // =============================
  // PAGAMENTOS DO CLIENTE
  // =============================

  const clienteSelecionado = clienteIdSelecionado
    ? getCliente(clienteIdSelecionado)
    : null;

  const planoCliente = clienteSelecionado
    ? getPlano(clienteSelecionado.plano_id)
    : null;

  const pagamentosDoCliente = clienteSelecionado
    ? pagamentos
        .filter((p) => Number(p.cliente_id) === Number(clienteIdSelecionado))
        .sort((a, b) => (a.mes_referencia > b.mes_referencia ? -1 : 1))
    : [];

  const mesesPagos = pagamentosDoCliente.filter((p) =>
    (p.status || "").toLowerCase().includes("pago")
  ).length;

  const mesesIsentos = pagamentosDoCliente.filter((p) =>
    (p.status || "").toLowerCase().includes("isento")
  ).length;

  const mesesAguardando = pagamentosDoCliente.filter((p) =>
    (p.status || "").toLowerCase().includes("aguardando")
  ).length;

  // =============================
  // RENDER
  // =============================

  return (
    <div className="pagamentos-container">
      <h3>Listagem de Pagamentos</h3>

      <div className="filtros-pagamentos">
        <label>
          Cliente:&nbsp;
          <select
            value={clienteIdSelecionado}
            onChange={(e) => setClienteIdSelecionado(e.target.value)}
          >
            <option value="">Selecione um cliente...</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome} ({c.email})
              </option>
            ))}
          </select>
        </label>

        <button onClick={carregarTudo}>Recarregar</button>
      </div>

      {carregando && <p>Carregando...</p>}
      {msg && <p style={{ color: "red" }}>{msg}</p>}

      {!clienteSelecionado && !carregando && (
        <p style={{ marginTop: 10 }}>
          Selecione um cliente acima para visualizar o histórico de pagamentos.
        </p>
      )}

      {clienteSelecionado && (
        <>
          {/* RESUMO DO CLIENTE */}
          <div className="card-resumo-cliente">
            <h4>{clienteSelecionado.nome}</h4>
            <div className="texto-menor">{clienteSelecionado.email}</div>
            {clienteSelecionado.phone && (
              <div className="texto-menor">Tel: {clienteSelecionado.phone}</div>
            )}
            {clienteSelecionado.cpf && (
              <div className="texto-menor">CPF: {clienteSelecionado.cpf}</div>
            )}

            <div style={{ marginTop: 8 }}>
              <strong>Plano: </strong>
              {planoCliente ? planoCliente.nome : "—"}
              {planoCliente && (
                <span className="texto-menor">
                  {" "}
                  | Slots incluídos: {planoCliente.slots_inclusos}
                </span>
              )}
            </div>

            <div>
              <strong>Slots contratados: </strong>
              {clienteSelecionado.slots_contratados}
            </div>

            <div style={{ marginTop: 6 }}>
              <strong>Resumo de meses:</strong>{" "}
              <span className="texto-menor">
                Pagos: {mesesPagos} | Isentos: {mesesIsentos} | Aguardando:{" "}
                {mesesAguardando}
              </span>
            </div>
          </div>

          {/* TABELA */}
          <table className="tabela-pagamentos">
            <thead>
              <tr>
                <th>Mês ref.</th>
                <th>Data pagamento</th>
                <th>Slots incluídos</th>
                <th>Slots cobrados</th>
                <th>Extras</th>
                <th>Valor calculado</th>
                <th>Valor pago</th>
                <th>Status</th>
                <th>Método</th>
                <th>Descrição</th>
                <th>Ação</th>
              </tr>
            </thead>

            <tbody>
              {pagamentosDoCliente.map((p) => {
                const status = p.status || "—";
                const isPago = status.toLowerCase().includes("pago");
                const isIsento = status.toLowerCase().includes("isento");

                const cliente = getCliente(p.cliente_id);
                const plano = cliente ? getPlano(cliente.plano_id) : null;

                const slotsIncluidos = plano
                  ? Number(plano.slots_inclusos || 0)
                  : 0;
                const slotsCobrados =
                  p.slots_cobrados != null
                    ? Number(p.slots_cobrados)
                    : cliente
                    ? Number(cliente.slots_contratados || 0)
                    : 0;

                const extras = Math.max(slotsCobrados - slotsIncluidos, 0);

                const valorCalculado = computeValorCalculado(p);

                return (
                  <tr key={p.id}>
                    <td>{p.mes_referencia}</td>
                    <td>{formatData(p.data_pagamento)}</td>

                    <td>{slotsIncluidos}</td>
                    <td>{slotsCobrados}</td>
                    <td>{extras}</td>

                    <td>R$ {Number(valorCalculado || 0).toFixed(2)}</td>

                    <td>
                      {p.valor != null ? (
                        <b>R$ {Number(p.valor).toFixed(2)}</b>
                      ) : (
                        <span style={{ opacity: 0.5 }}>—</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={
                          isPago
                            ? "badge badge-ativo"
                            : isIsento
                            ? "badge badge-isento"
                            : "badge badge-inativo"
                        }
                      >
                        {status}
                      </span>
                    </td>
                    <td>{p.metodo_pagamento || "—"}</td>
                    <td className="texto-menor">{p.descricao || ""}</td>
                    <td>
                      {!isPago && !isIsento && (
                        <button
                          className="btn-pagar"
                          onClick={() => marcarComoPago(p)}
                        >
                          Marcar como pago (PIX)
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {pagamentosDoCliente.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: "center" }}>
                    Nenhum pagamento cadastrado para este cliente.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <p className="texto-menor" style={{ marginTop: 8 }}>
            Para cadastrar pagamentos atrasados ou adiantados, use a tela de{" "}
            <strong>Cadastro de Pagamentos</strong>, escolhendo o{" "}
            <code>mes_referencia</code> correto.
          </p>
        </>
      )}
    </div>
  );
};

export default ListarPagamentos;
